import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// Importa tus iconos
import VSCIcon from '../assets/appsused/vsc.svg';
import AndroidIcon from '../assets/appsused/android.png';
import FigmaIcon from '../assets/appsused/figma.svg';
import TeamsIcon from '../assets/appsused/teams.svg';
import NotionIcon from '../assets/appsused/notion.svg';
import XcodeIcon from '../assets/appsused/xcode.svg';
import GoogleWorkspaceIcon from '../assets/appsused/gsuit.svg';
import ClickUpIcon from '../assets/appsused/clickup.svg';

const techApps = [
  { 
    id: 1, 
    name: 'Visual Studio Code', 
    icon: VSCIcon,
    description: 'Editor de texto multiplataforma con soporte para extensiones, ideal para desarrollo web, Node, etc.',
    url: 'https://code.visualstudio.com/'
  },
  { 
    id: 2, 
    name: 'Android Studio', 
    icon: AndroidIcon,
    description: 'Entorno de desarrollo integrado (IDE) oficial para crear apps Android.',
    url: 'https://developer.android.com/studio'
  },
  { 
    id: 3, 
    name: 'Figma', 
    icon: FigmaIcon,
    description: 'Herramienta colaborativa para diseñar interfaces de usuario y prototipos.',
    url: 'https://www.figma.com/'
  },
  { 
    id: 4, 
    name: 'Teams', 
    icon: TeamsIcon,
    description: 'Aplicación de comunicación empresarial y colaboración en equipo.',
    url: 'https://www.microsoft.com/microsoft-teams/'
  },
  { 
    id: 5, 
    name: 'Notion', 
    icon: NotionIcon,
    description: 'Plataforma para organización personal y de equipo, con notas, bases de datos y más.',
    url: 'https://www.notion.com/es-es'
  },
  { 
    id: 6, 
    name: 'Xcode', 
    icon: XcodeIcon,
    description: 'Entorno de desarrollo integrado para macOS, usado para desarrollar software para iOS, iPadOS, macOS, watchOS y tvOS.',
    url: 'https://developer.apple.com/xcode/'
  },
  { 
    id: 7, 
    name: 'Google Workspace', 
    icon: GoogleWorkspaceIcon,
    description: 'Suite de herramientas de productividad que incluye Gmail, Drive, Docs, Sheets, Meet y otras aplicaciones colaborativas.',
    url: 'https://workspace.google.com/'
  },
  { 
    id: 8, 
    name: 'ClickUp', 
    icon: ClickUpIcon,
    description: 'Herramienta de gestión de proyectos que puede utilizarse para diversas tareas, desde la gestión de un equipo hasta el seguimiento de objetivos personales.',
    url: 'https://clickup.com'
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 12,
    },
  },
  hover: {
    y: -5,
    transition: { duration: 0.2 }
  }
};


const Tech = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const startX = useRef(0);
  const isDragging = useRef(false);

  // Navegación del carrusel
  const goToPrev = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : techApps.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev < techApps.length - 1 ? prev + 1 : 0));
  };

  // Manejo de gestos táctiles para el carrusel horizontal
  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
    document.body.style.overflow = 'hidden'; // Bloquear scroll de la página
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.body.style.overflow = '';
    
    const endX = e.changedTouches[0].clientX;
    const diffX = startX.current - endX;
    const threshold = 50; // Mínimo desplazamiento para cambiar de slide

    if (diffX > threshold) {
      goToNext();
    } else if (diffX < -threshold) {
      goToPrev();
    }
  };

  // Configurar event listeners
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.addEventListener('touchstart', handleTouchStart, { passive: false });
    carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
    carousel.addEventListener('touchend', handleTouchEnd);

    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  });

  return (
    <motion.div
      className="p-4 md:p-6 bg-transparent rounded-lg"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2
        className="text-2xl md:text-3xl font-bold text-pastel-yellow mb-6 md:mb-8 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Aplicaciones que utilizo
      </motion.h2>

      {/* Carrusel horizontal para móviles */}
      <div className="md:hidden">
        <div 
          ref={carouselRef}
          className="relative overflow-hidden"
        >
          <motion.div
            className="flex"
            animate={{ x: `-${currentIndex * 100}%` }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {techApps.map((app) => (
              <div key={app.id} className="w-full flex-shrink-0 px-2">
                <motion.div 
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <TechCard 
                    name={app.name} 
                    icon={app.icon} 
                    description={app.description} 
                    url={app.url} 
                  />
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
        
        {/* Controles de navegación e indicadores */}
        <div className="flex justify-center items-center mt-4 space-x-4">
          <button 
            onClick={goToPrev}
            className="text-pastel-yellow p-2"
            aria-label="Aplicación anterior"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="flex space-x-2">
            {techApps.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full ${
                  currentIndex === index ? 'bg-pastel-yellow' : 'bg-neutral-700'
                }`}
                aria-label={`Ir a aplicación ${index + 1}`}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>
          
          <button 
            onClick={goToNext}
            className="text-pastel-yellow p-2"
            aria-label="Siguiente aplicación"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Grid normal para escritorio (se mantiene igual) */}
      <motion.div
        className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5"
        variants={containerVariants}
      >
        {techApps.map((app) => (
          <motion.div 
            key={app.id} 
            variants={cardVariants}
            whileHover="hover"
            className="h-full"
          >
            <TechCard 
              name={app.name} 
              icon={app.icon} 
              description={app.description} 
              url={app.url} 
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default Tech;

const TechCard = ({ name, icon, description, url }) => {
  return (
    <motion.div
      className="group relative h-full"
      variants={cardVariants}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block p-4 md:p-5 h-full bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl border border-neutral-700 no-underline transition-all duration-300 group-hover:border-pastel-yellow/50 flex flex-col"
      >
        <div className="flex items-start mb-3 md:mb-4">
          <div className="p-2 bg-neutral-700 rounded-lg mr-3 border border-neutral-700">
            <img 
              src={icon} 
              alt={name} 
              className="w-7 h-7 md:w-8 md:h-8 object-contain" 
            />
          </div>
          <h3 className="text-white font-bold text-base md:text-lg">{name}</h3>
        </div>
        
        {/* Descripción flexible */}
        <p className="text-gray-400 text-sm mb-4 flex-grow">
          {description}
        </p>
        
        <div className="flex justify-between items-center">
          <span className="text-xs text-pastel-yellow opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Click para visitar
          </span>
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-black/30 group-hover:bg-pastel-yellow/20 transition-colors duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 group-hover:text-pastel-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        </div>
      </a>
      
      <motion.div
        className="absolute inset-0 rounded-xl shadow-lg shadow-pastel-yellow/0 z-[-1] group-hover:shadow-pastel-yellow/20"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
};